import { TrackedButton, TrackedDiv } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CenterAlignedContainer } from '../CenterAlignedContainer';

import styles from './Tabs.module.css';
import { TabsProvider, useTabs } from './TabsContext';

import type React from 'react';
import type { ReactNode } from 'react';

interface TabListProps {
  children: ReactNode;
  className?: string;
}

interface TabProps {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  className?: string;
}

interface TabsContainerProps {
  children: ReactNode;
  defaultTab: string;
  className?: string;
}

/**
 * Outer container for tabs list where both the tabs and the indicator are rendered
 * and now includes blur overlays for scroll feedback.
 */
export const List: React.FC<TabListProps> = ({ children, className = '' }) => {
  const { activeTab } = useTabs();
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});
  const [showLeftBlur, setShowLeftBlur] = useState(false);
  const [showRightBlur, setShowRightBlur] = useState(false);

  // Get meta information for the tab container & active tab
  const getTabsMeta = () => {
    const tabsNode = tabListRef.current;
    let tabsMeta;

    if (tabsNode) {
      const rect = tabsNode.getBoundingClientRect();

      tabsMeta = {
        clientWidth: tabsNode.clientWidth,
        scrollLeft: tabsNode.scrollLeft,
        scrollWidth: tabsNode.scrollWidth,
        left: rect.left,
        top: rect.top,
      };
    }

    let tabMeta;
    const tabList = tabListRef.current;
    if (tabList) {
      // FIXME: This is a workaround to get the active tab meta
      // will implement a way to match the active tab with the children
      const activeTabEl = tabList.querySelector(`.${styles.active}`);
      if (activeTabEl instanceof HTMLElement) {
        tabMeta = activeTabEl.getBoundingClientRect();
      }
    }
    return { tabsMeta, tabMeta };
  };

  // Update the indicator position using current meta
  const updateIndicator = useCallback(() => {
    const { tabsMeta, tabMeta } = getTabsMeta();
    let startValue = 0;

    if (tabsMeta && tabMeta) {
      startValue = tabMeta.left - tabsMeta.left + tabsMeta.scrollLeft;
    }

    const newIndicatorStyle = {
      left: startValue,
      width: tabMeta?.width || 0,
    };

    if (typeof indicatorStyle.left !== 'number' || typeof indicatorStyle.width !== 'number') {
      setIndicatorStyle(newIndicatorStyle);
    } else {
      const dStart = Math.abs(indicatorStyle.left - newIndicatorStyle.left);
      const dSize = Math.abs(indicatorStyle.width - newIndicatorStyle.width);

      if (dStart >= 1 || dSize >= 1) {
        setIndicatorStyle(newIndicatorStyle);
      }
    }
  }, [indicatorStyle]);

  const updateBlur = () => {
    if (!tabsRef.current) return;
    const { scrollLeft, clientWidth, scrollWidth } = tabsRef.current;
    const isScrollable = scrollWidth > clientWidth;
    const isScrolledToStart = scrollLeft <= 0;
    const isScrolledToEnd = scrollLeft + clientWidth >= scrollWidth;

    setShowLeftBlur(!isScrolledToStart);
    setShowRightBlur(!isScrolledToEnd && isScrollable);
  };

  const handleScroll = debounce(() => {
    updateBlur();
    updateIndicator();
  }, 10);

  useEffect(() => {
    const handleResize = debounce(() => {
      updateIndicator();
      updateBlur();
    }, 100);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && tabsRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(tabsRef.current);
    }

    updateBlur();
    updateIndicator();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      handleScroll.cancel();
      handleResize.cancel();
    };
  }, [updateIndicator]);

  useEffect(() => {
    updateIndicator();
  }, [activeTab, updateIndicator]);

  return (
    <div
      className={clsx(
        styles.tabsContainer,
        className,
        showLeftBlur && styles.showLeft,
        showRightBlur && styles.showRight,
      )}
      role='tablist'
    >
      <div ref={tabsRef} role='tablist' onScroll={handleScroll} className={clsx(styles.tabList, className)}>
        <div ref={tabListRef} className={styles.tabListInner}>
          {children}
          <div className={styles.tabIndicatorTrack} style={indicatorStyle} />
        </div>
      </div>
    </div>
  );
};

/**
 * Individual tab component
 * @param id - unique id for the tab
 * @param label - tab label
 * @param icon - optional icon to be displayed next to the label
 * @param className - additional classes to be applied to the tab
 * TODO: right now returns a simple button. maybe we should add tracking
 */
const Tab: React.FC<TabProps> = ({ id, label, icon, className = '' }) => {
  const { activeTab, setActiveTab } = useTabs();
  const tabRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    setActiveTab(id);
  };

  const isActive = activeTab === id;

  return (
    <TrackedButton
      type='button'
      objectiv={{ id: `tab-${id}` }}
      ref={tabRef}
      className={`${styles.tab} ${isActive ? styles.active : ''} ${className}`}
      onClick={handleClick}
      role='tab'
      aria-selected={isActive}
      id={`tab-${id}`}
      aria-controls={`tabpanel-${id}`}
    >
      {icon && <span className={styles.tabIcon}>{icon}</span>}
      <span className={styles.tabLabel}>{label}</span>
    </TrackedButton>
  );
};

interface TabPanelProps {
  id: string;
  children: ReactNode;
  className?: string;
}

/**
 * Contents for each tab - displays value only when selected.
 * TODO: Should get more features like lazy loading, force mount, memoized mounting
 * Another TODO: Specialized for MyInsify but should be more generic if used in design system
 * i.e. should not have a default container, `CenterAlignedContainer` in this case
 * @param id - unique id for the tab panel
 * @param children - content to be displayed in the tab panel
 * @param className - additional classes to be applied to the tab panel
 */
const Panel: React.FC<TabPanelProps> = ({ id, children, className = '' }) => {
  const { activeTab } = useTabs();
  const isActive = activeTab === id;

  return (
    <CenterAlignedContainer>
      <TrackedDiv
        role='tabpanel'
        id={`tabpanel-${id}`}
        objectiv={{ id: `tabpanel-${id}` }}
        aria-labelledby={`tab-${id}`}
        hidden={!isActive}
        className={`${styles.tabPanel} ${isActive ? styles.activePanel : ''} ${className}`}
      >
        {isActive && children}
      </TrackedDiv>
    </CenterAlignedContainer>
  );
};

const Container: React.FC<TabsContainerProps> = ({ children, defaultTab }) => {
  return <TabsProvider defaultTab={defaultTab}>{children}</TabsProvider>;
};

export const Tabs = {
  List,
  Tab,
  Panel,
  Container,
};
